import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { defaultPageSize } from '../../config/defaultPagination'
import { useGetAcronymsQuery } from '../../store/redux/reducers/projectsSlice'
import { Project } from '../../store/types'
import { AutocompleteFetch } from '../Autocomplete/AutocompleteFetch'
import { SxProps } from '@mui/material'

type AutocompleteFavoriteProjectsProps = {
  favoriteProjects: Project[]
  favoriteProjectsChange: (option: Project[]) => void
  sxProps?: SxProps
}

const AutocompleteFavoriteProjects = ({
  favoriteProjects,
  favoriteProjectsChange,
  sxProps,
}: AutocompleteFavoriteProjectsProps) => {
  const [projects, setProjects] = useState<Map<string, Project>>(new Map())
  const [page, setPage] = useState(0)
  const [searchedAcronym, setSearchedAcronym] = useState('')
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const projectsProps = useCallback(() => {
    return {
      page: page,
      size: defaultPageSize,
      acronym: searchedAcronym,
      loggable: true,
    }
  }, [page, searchedAcronym])

  const { data: acronymsResponse } = useGetAcronymsQuery(projectsProps())

  useEffect(() => {
    if (acronymsResponse) {
      setProjects(prev => {
        const updated = new Map(prev)
        acronymsResponse.content.forEach(projectAcronym => {
          if (!updated.has(projectAcronym.id)) {
            updated.set(projectAcronym.id, {
              id: projectAcronym.id,
              acronym: projectAcronym.acronym,
              name: projectAcronym.name,
              projectStatus: projectAcronym.projectStatus,
              favorite: projectAcronym.favorite,
            } as Project)
          }
        })
        return updated
      })
    }
  }, [acronymsResponse, page, searchedAcronym])

  useEffect(() => {
    if (acronymsResponse?.content) {
      if (!acronymsResponse.last) {
        setPage(prev => prev + 1)
      }
    }
  }, [acronymsResponse])

  const handleSearchInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    timeoutRef.current = setTimeout(() => {
      const newSearchTerm = event.target.value
      setSearchedAcronym(newSearchTerm)
      setPage(0)
      if (newSearchTerm === '') {
        setProjects(new Map())
      }
    }, 500)
  }, [])

  const options = useMemo(() => {
    const projectsList = Array.from(projects.values())
    const existingIds = new Set(favoriteProjects.map(p => p.id))
    const missingFavorites = favoriteProjects.filter(fav => !existingIds.has(fav.id))
    return [...projectsList, ...missingFavorites]
  }, [projects, favoriteProjects])

  return (
    <AutocompleteFetch<Project>
      selectedItems={favoriteProjects}
      setSelectedItems={favoriteProjectsChange}
      options={options}
      getLabel={project => `${project.acronym}`}
      getId={project => project.id}
      labelTranslationKey="components.settingsDialog.favoriteProjects"
      onSearchChange={handleSearchInputChange}
      width={'60%'}
      sxProps={sxProps}
    />
  )
}

export default AutocompleteFavoriteProjects
